/**
 * ⌘K command palette — DESIGN.md §13.5, §12.4.
 *
 * "⌘K command palette (search cards/projects with the §12.4 operators, quick
 * actions: 'New card', 'Go to project…')."
 *
 * It is the keyboard's front door, so it does three jobs at once (SHELL-5,
 * KEYS-11, DAILY-23): it *goes* anywhere in the product, it *does* the handful
 * of global actions, and it searches. Written by hand rather than pulled from
 * a library because the interesting part — showing which §12.4 operators the
 * server understood (R43) — is not something a generic palette provides.
 *
 * Opening is not its own concern: `GlobalHotkeys` owns the key table and emits
 * the `palette` UI event, so ⌘K and `/` cannot drift apart, and the palette
 * cannot stack itself on top of a dialog the user is in the middle of
 * (KEYS-19).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowRight,
  BarChart3,
  CheckSquare,
  FolderKanban,
  Inbox,
  Keyboard,
  LayoutGrid,
  LogOut,
  MessageSquare,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Shield,
  SquareCheck,
  SunMoon,
  TimerOff,
  type LucideIcon,
} from 'lucide-react';
import { useDebouncedValue, useSearch } from '@/features/search/api';
import { useProjects } from '@/features/projects/api';
import { useTimers, useTimerControls } from '@/features/time/api';
import { useAuth, useLogout } from '@/lib/auth';
import { currentFocusOrigin, isOverlayOpen, returnFocus } from '@/lib/hotkeys';
import { readPref, writePref } from '@/lib/prefs';
import { useTheme } from '@/lib/theme';
import { emitUi, onUi } from '@/lib/ui-events';
import { cn } from '@/lib/utils';
import { newCardTarget, useCurrentProjectKey } from '@/lib/current-project';

type Group = 'Recent' | 'Actions' | 'Cards' | 'Comments' | 'Projects' | 'Search';

interface Item {
  id: string;
  group: Group;
  label: string;
  hint?: string;
  /** Extra words the fuzzy match should consider (synonyms, project keys). */
  keywords?: string;
  /** The global shortcut that does the same thing, shown on the row. */
  shortcut?: string;
  icon: LucideIcon;
  run: () => void;
  /** How to offer this again next time; omitted for rows worth not repeating. */
  recent?: Recent;
}

/* ------------------------------------------------------------------------ */
/* Recents                                                                    */
/* ------------------------------------------------------------------------ */

interface Recent {
  kind: 'command' | 'card' | 'project';
  id: string;
  label: string;
  hint?: string;
  number?: number;
  key?: string;
}

const RECENT_KEY = 'slate.ui.palette-recent';
const RECENT_LIMIT = 5;

function readRecents(): Recent[] {
  return (
    readPref<Recent[]>(RECENT_KEY, (raw) => {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return undefined;
      return parsed.filter(
        (entry): entry is Recent =>
          typeof entry === 'object' && entry !== null && typeof (entry as Recent).id === 'string',
      );
    }) ?? []
  );
}

function pushRecent(entry: Recent): void {
  const next = [entry, ...readRecents().filter((old) => old.id !== entry.id)].slice(
    0,
    RECENT_LIMIT,
  );
  writePref(RECENT_KEY, JSON.stringify(next));
}

/* ------------------------------------------------------------------------ */
/* Matching                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Higher is better; `null` means no match. Deliberately simple and explainable
 * — prefix beats word-prefix beats initials beats substring beats subsequence
 * — because a palette that ranks by a score nobody can predict feels broken.
 * Subsequence is what makes `clb` find "Collaborating" (KEYS-11).
 */
export function scoreMatch(text: string, query: string): number | null {
  const needle = query.toLowerCase().replace(/\s+/g, '');
  if (needle === '') return 0;
  const hay = text.toLowerCase();

  if (hay.startsWith(needle)) return 1000 - hay.length;

  const words = hay.split(/[^a-z0-9#]+/).filter(Boolean);
  if (words.some((word) => word.startsWith(needle))) return 800 - hay.length;

  const initials = words.map((word) => word[0] ?? '').join('');
  if (initials.startsWith(needle)) return 700 - hay.length;

  if (hay.includes(needle)) return 600 - hay.length;

  let cursor = 0;
  let gaps = 0;
  let previous = -1;
  for (const char of needle) {
    const index = hay.indexOf(char, cursor);
    if (index === -1) return null;
    if (previous >= 0) gaps += index - previous - 1;
    previous = index;
    cursor = index + 1;
  }
  return 400 - gaps;
}

/* ------------------------------------------------------------------------ */

export function CommandPalette(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { cycleMode } = useTheme();
  const logout = useLogout();
  const timers = useTimers();
  const { stop } = useTimerControls();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [recents, setRecents] = useState<Recent[]>([]);

  // KEYS-17 — one request when the typing stops, not one per character.
  const debouncedQuery = useDebouncedValue(query, 200);
  const { data, isFetching } = useSearch(debouncedQuery, { enabled: open, limit: 8 });
  const { data: projects } = useProjects('active');

  const close = (): void => {
    setOpen(false);
    setQuery('');
  };

  useEffect(
    () =>
      onUi('palette', (detail) => {
        // KEYS-19 — never stack on a dialog the user is filling in; opening
        // here and navigating away would discard what they typed.
        if (isOverlayOpen()) return;
        openerRef.current = currentFocusOrigin();
        setRecents(readRecents());
        setQuery(detail?.query ?? '');
        setCursor(0);
        setOpen(true);
      }),
    [],
  );

  const projectKey = useCurrentProjectKey();

  /**
   * Every route and every global action, always present. §13.5 asks for "quick
   * actions"; the review found the palette could not reach a single route
   * (SHELL-5), which is the one thing a palette is for.
   */
  const commands = useMemo<Item[]>(() => {
    const go = (
      id: string,
      label: string,
      icon: LucideIcon,
      run: () => void,
      keywords?: string,
    ): Item => ({
      id,
      group: 'Actions',
      label,
      hint: 'Go',
      icon,
      keywords,
      run,
      recent: { kind: 'command', id, label, hint: 'Go' },
    });

    const list: Item[] = [
      go(
        'go-my-tasks',
        'My Tasks',
        CheckSquare,
        () => void navigate({ to: '/' }),
        'home assigned to me',
      ),
      go(
        'go-inbox',
        'Inbox',
        Inbox,
        () => void navigate({ to: '/inbox' }),
        'notifications unread activity',
      ),
      go('go-search', 'Search', Search, () => void navigate({ to: '/search' }), 'find operators'),
      go('go-views', 'Views', LayoutGrid, () => void navigate({ to: '/views' }), 'saved filters'),
      go(
        'go-projects',
        'Projects',
        FolderKanban,
        () => void navigate({ to: '/projects' }),
        'boards',
      ),
      go(
        'go-reports',
        'Reports',
        BarChart3,
        () => void navigate({ to: '/reports' }),
        'dashboards charts',
      ),
    ];
    if (isAdmin) {
      list.push(
        go(
          'go-admin',
          'Admin',
          Shield,
          () => void navigate({ to: '/admin/users' }),
          'users groups lookups tokens',
        ),
      );
    }
    list.push(
      go(
        'go-settings',
        'Settings',
        Settings,
        () => void navigate({ to: '/settings' }),
        'appearance profile theme text size',
      ),
    );

    list.push({
      id: 'new-card',
      group: 'Actions',
      label: 'New card',
      hint: projectKey ? `in ${projectKey}` : 'Create',
      icon: Plus,
      shortcut: 'c',
      keywords: 'create task add',
      run: () => emitUi('new-card', newCardTarget(projectKey)),
      recent: { kind: 'command', id: 'new-card', label: 'New card' },
    });

    list.push({
      id: 'toggle-theme',
      group: 'Actions',
      label: 'Toggle theme',
      hint: 'Dark · Light · System',
      icon: SunMoon,
      keywords: 'dark light appearance contrast',
      run: cycleMode,
      recent: { kind: 'command', id: 'toggle-theme', label: 'Toggle theme' },
    });

    list.push({
      id: 'toggle-sidebar',
      group: 'Actions',
      label: 'Collapse or expand the sidebar',
      hint: 'Layout',
      icon: PanelLeft,
      shortcut: '[',
      keywords: 'hide show navigation width',
      run: () => document.querySelector<HTMLElement>('[data-sidebar-toggle]')?.click(),
      recent: { kind: 'command', id: 'toggle-sidebar', label: 'Collapse or expand the sidebar' },
    });

    // One entry per running timer (#4): with several going, "Stop timer" is
    // ambiguous, and the palette is exactly where you reach when you cannot
    // remember what you left running.
    for (const timer of timers.data ?? []) {
      list.push({
        id: `stop-timer-${timer.taskId}`,
        group: 'Actions',
        label: `Stop timer on #${timer.taskNumber} ${timer.taskTitle}`,
        hint: 'Logs the time',
        icon: TimerOff,
        keywords: 'time tracking clock',
        run: () => stop.mutate({ taskId: timer.taskId }),
      });
    }

    list.push({
      id: 'help',
      group: 'Actions',
      label: 'Keyboard shortcuts',
      hint: 'Help',
      icon: Keyboard,
      shortcut: '?',
      keywords: 'keys hotkeys cheatsheet',
      run: () => emitUi('help'),
    });

    list.push({
      id: 'sign-out',
      group: 'Actions',
      label: 'Sign out',
      hint: 'Session',
      icon: LogOut,
      keywords: 'log out leave',
      run: () => logout.mutate(),
    });

    // §13.5's "Go to project…" — cheap to offer, and the only way to reach a
    // project by name without loading /projects first.
    for (const project of projects?.items ?? []) {
      list.push({
        id: `project-${project.id}`,
        group: 'Projects',
        label: project.name,
        hint: project.key,
        keywords: project.key,
        icon: FolderKanban,
        run: () => void navigate({ to: '/projects/$key', params: { key: project.key } }),
        recent: {
          kind: 'project',
          id: `project-${project.id}`,
          label: project.name,
          key: project.key,
        },
      });
    }

    return list;
  }, [isAdmin, projectKey, projects, navigate, cycleMode, logout, timers.data, stop]);

  /** Server-side hits: cards, comments, and projects the local list missed. */
  const results = useMemo<Item[]>(() => {
    const out: Item[] = [];
    if (query.trim() === '') return out;

    const card = (
      number: number,
      title: string,
      hint: string,
      id: string,
      group: Group = 'Cards',
    ): Item => ({
      id,
      group,
      label: `#${number} ${title}`,
      hint,
      icon: SquareCheck,
      run: () => void navigate({ to: '/card/$number', params: { number: String(number) } }),
      recent: { kind: 'card', id, label: `#${number} ${title}`, hint, number },
    });

    // An exact `#number` is the answer, not a result — §12.4 short-circuits.
    if (data?.exactCard) {
      out.push(
        card(
          data.exactCard.number,
          data.exactCard.title,
          'Open card',
          `exact-${data.exactCard.id}`,
        ),
      );
    }
    for (const task of data?.tasks ?? []) {
      if (data?.exactCard?.id === task.id) continue;
      out.push(card(task.number, task.title, task.statusName, `card-${task.id}`));
    }
    for (const comment of data?.comments ?? []) {
      out.push({
        id: `comment-${comment.id}`,
        group: 'Comments',
        label: comment.snippet,
        hint: `#${comment.task.number} · ${comment.author.name}`,
        icon: MessageSquare,
        run: () =>
          void navigate({
            to: '/card/$number',
            params: { number: String(comment.task.number) },
          }),
      });
    }
    for (const project of data?.projects ?? []) {
      if (commands.some((item) => item.id === `project-${project.id}`)) continue;
      out.push({
        id: `found-project-${project.id}`,
        group: 'Projects',
        label: project.name,
        hint: project.key,
        icon: FolderKanban,
        run: () => void navigate({ to: '/projects/$key', params: { key: project.key } }),
      });
    }
    return out;
  }, [data, query, navigate, commands]);

  /** The recents list, re-hydrated against the live command table. */
  const recentItems = useMemo<Item[]>(() => {
    if (query.trim() !== '') return [];
    const out: Item[] = [];
    for (const entry of recents) {
      if (entry.kind === 'command' || entry.kind === 'project') {
        const command = commands.find((item) => item.id === entry.id);
        if (command) out.push({ ...command, group: 'Recent' });
        continue;
      }
      if (entry.kind === 'card' && entry.number !== undefined) {
        const number = entry.number;
        out.push({
          id: `recent-${entry.id}`,
          group: 'Recent',
          label: entry.label,
          hint: entry.hint,
          icon: SquareCheck,
          run: () => void navigate({ to: '/card/$number', params: { number: String(number) } }),
          recent: entry,
        });
      }
    }
    return out;
  }, [recents, commands, query, navigate]);

  /**
   * Grouped for reading (DAILY-23) and flattened for the arrow keys in one
   * pass, so the two can never disagree about which row is row 0 — and groups
   * are ordered by their best match, so what you typed is what is at the top.
   */
  const sections = useMemo(() => {
    const trimmed = query.trim();
    const buckets = new Map<Group, { item: Item; score: number }[]>();
    const add = (item: Item, score: number): void => {
      const bucket = buckets.get(item.group);
      if (bucket) bucket.push({ item, score });
      else buckets.set(item.group, [{ item, score }]);
    };

    if (trimmed === '') {
      for (const item of recentItems) add(item, 0);
      for (const item of commands) add(item, 0);
    } else {
      for (const item of commands) {
        const score = scoreMatch(`${item.label} ${item.keywords ?? ''}`, trimmed);
        if (score !== null) add(item, score);
      }
      // Server hits arrive already ranked; these scores only say how strongly
      // the group as a whole competes with a local match.
      for (const item of results) {
        add(item, item.group === 'Cards' ? (data?.exactCard ? 1200 : 650) : 350);
      }
      // SHELL-5 — "0 results dead end". There is always one more thing to try.
      add(
        {
          id: 'full-search',
          group: 'Search',
          label: `Search for \u201c${trimmed}\u201d`,
          hint: 'All results',
          icon: ArrowRight,
          run: () => void navigate({ to: '/search', search: { q: trimmed } }),
        },
        -1,
      );
    }

    for (const bucket of buckets.values()) bucket.sort((a, b) => b.score - a.score);

    const rank = (group: Group): number => (group === 'Recent' ? -1 : group === 'Search' ? 1 : 0);
    const best = (group: Group): number => buckets.get(group)?.[0]?.score ?? 0;
    const ORDER: Group[] = ['Recent', 'Actions', 'Cards', 'Comments', 'Projects', 'Search'];
    const groups = [...buckets.keys()].sort(
      (a, b) => rank(a) - rank(b) || best(b) - best(a) || ORDER.indexOf(a) - ORDER.indexOf(b),
    );

    let index = 0;
    return groups.map((group) => ({
      group,
      entries: (buckets.get(group) ?? []).map(({ item }) => ({ item, index: index++ })),
    }));
  }, [query, commands, results, recentItems, data?.exactCard, navigate]);

  /** The same rows, flat — this is what the arrow keys and Enter walk. */
  const items = useMemo<Item[]>(
    () => sections.flatMap((section) => section.entries.map((entry) => entry.item)),
    [sections],
  );

  useEffect(() => setCursor(0), [query]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${cursor}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open, items.length]);

  const run = (item: Item | undefined): void => {
    if (!item) return;
    if (item.recent) pushRecent(item.recent);
    close();
    item.run();
  };

  const move = (delta: number): void => {
    if (items.length === 0) return;
    setCursor((value) => (value + delta + items.length) % items.length);
  };

  const activeId = items[cursor] ? `palette-option-${cursor}` : undefined;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <Dialog.Portal>
        {/*
          #49 — the palette used to appear and vanish instantly, while every
          other dialog in the app fades (`DialogContent`). It is not using the
          shared component because its content is nothing like a dialog's, so
          it did not inherit the transition either. Radix keeps the element
          mounted for the length of the `closed` animation, which is what makes
          the way *out* animate as well as the way in.
        */}
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 duration-150" />
        {/*
          The accessible name comes from Dialog.Title — Radix wires
          `aria-labelledby` to it — so there is deliberately no competing
          `aria-label` here.
        */}
        <Dialog.Content
          onCloseAutoFocus={(event) => returnFocus(event, openerRef.current)}
          className="bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 fixed left-1/2 top-[8vh] z-50 flex max-h-[84dvh] w-[calc(100%-1.5rem)] max-w-2xl -translate-x-1/2 flex-col overflow-hidden rounded-lg border shadow-xl duration-150"
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>

          {/*
            #48 — the field is autofocused, and a text input always matches
            `:focus-visible`, so index.css's global `ring-2 ring-offset-2` was
            drawing a ring 4px outside a box sitting flush against the modal's
            top edge. `overflow-hidden` (which the rounded corners need) then
            sliced the top of it off, leaving three sides of a highlight.

            So the highlight moves off the input and onto the row, which has
            room for it: a real field with a border, inset far enough that the
            whole ring is inside the modal.
          */}
          <div className="border-b p-2">
            <div className="border-input focus-within:ring-ring focus-within:ring-offset-background flex items-center gap-2 rounded-md border px-3 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2">
              <Search className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              <input
                ref={inputRef}
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search or jump to…"
                aria-label="Search cards, projects and commands, or run a command"
                role="combobox"
                aria-expanded
                aria-controls="palette-listbox"
                aria-autocomplete="list"
                aria-activedescendant={activeId}
                // The ring belongs to the row now, so the input contributes
                // nothing of its own — `ring-0` beats the global rule in
                // index.css, which `outline-none` alone never did.
                className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    move(1);
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    move(-1);
                  } else if (event.key === 'Home') {
                    event.preventDefault();
                    setCursor(0);
                  } else if (event.key === 'End') {
                    event.preventDefault();
                    setCursor(Math.max(items.length - 1, 0));
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    run(items[cursor]);
                  }
                }}
              />
              {isFetching ? (
                <span className="text-muted-foreground shrink-0 text-xs" role="status">
                  Searching…
                </span>
              ) : null}
            </div>
          </div>

          {/*
            R43 — "unknown operators degrade to plain text, never error" is
            invisible unless the palette says what it understood. An
            unresolved term is a hint, not a failure.
          */}
          {data && data.interpretation.terms.length > 0 && query.trim() !== '' ? (
            <div className="flex flex-wrap gap-1 border-b px-3 py-1.5">
              {data.interpretation.terms.map((term, index) => (
                <span
                  key={`${term.operator}-${index}`}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-xs',
                    term.resolved
                      ? 'bg-primary/10 text-primary'
                      : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                  )}
                  title={term.resolved ? 'Applied' : 'Nothing matched — ignored'}
                >
                  {term.negated ? '-' : ''}
                  {term.operator}:{term.value}
                </span>
              ))}
            </div>
          ) : null}

          <div
            ref={listRef}
            id="palette-listbox"
            role="listbox"
            aria-label="Results"
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
          >
            {items.length === 0 ? (
              <p className="text-muted-foreground px-3 py-6 text-center text-sm">
                Nothing matched. Try fewer words, or a card number — 1042 — to jump straight to it.
              </p>
            ) : (
              sections.map((section) => (
                <div key={section.group} role="group" aria-label={section.group}>
                  {section.group === 'Search' ? null : (
                    <div className="text-muted-foreground px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide">
                      {section.group}
                    </div>
                  )}
                  {section.entries.map(({ item, index }) => (
                    <Row
                      key={item.id}
                      item={item}
                      index={index}
                      active={index === cursor}
                      onHover={() => setCursor(index)}
                      onPick={() => run(item)}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t px-3 py-1.5 text-xs">
            <span>↑↓ move · ↵ open · esc close</span>
            <span>Operators: assignee: status: tag: project: is:blocked due:today</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * A row is a `div`, not a `button`: a listbox option must not be in the tab
 * ring, and the input keeps focus so typing never breaks (KEYS-15). Click
 * still works, because `onClick` works on anything.
 */
function Row({
  item,
  index,
  active,
  onHover,
  onPick,
}: {
  item: Item;
  index: number;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <div
      id={`palette-option-${index}`}
      data-index={index}
      role="option"
      aria-selected={active}
      onMouseMove={onHover}
      onClick={onPick}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm',
        active && 'bg-accent',
      )}
    >
      <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.shortcut ? (
        <kbd className="bg-muted shrink-0 rounded border px-1.5 py-0.5 font-mono text-xs leading-none">
          {item.shortcut}
        </kbd>
      ) : null}
      {item.hint ? (
        <span className="text-muted-foreground max-w-[45%] shrink-0 truncate text-xs">
          {item.hint}
        </span>
      ) : null}
    </div>
  );
}
